import { useEffect, useState, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { auth, onAuthStateChanged, getAuthToken } from "@/lib/firebase";

// Pages
import HomePage from "@/pages/HomePage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import DashboardPage from "@/pages/DashboardPage";
import AdminPage from "@/pages/AdminPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import GamePage from "@/pages/GamePage";
import MemoriesPage from "@/pages/MemoriesPage";
import ProfilePage from "@/pages/ProfilePage";
import SeriesPage from "@/pages/SeriesPage";
import TripPage from "@/pages/TripPage";
import HostHomePage from "@/pages/HostHomePage";
import HostManageEventPage from "@/pages/HostManageEventPage";
import HostRevenuePage from "@/pages/HostRevenuePage";
import HostInboxPage from "@/pages/HostInboxPage";
import HostEventsPage from "@/pages/HostEventsPage";
import HostSchedulePage from "@/pages/HostSchedulePage";
import HostCustomersPage from "@/pages/HostCustomersPage";
import HostCustomerDetailPage from "@/pages/HostCustomerDetailPage";
import HostMembershipsPage from "@/pages/HostMembershipsPage";
import HostBillingPage from "@/pages/HostBillingPage";
import HostMarketingPage from "@/pages/HostMarketingPage";
import HostTeamPage from "@/pages/HostTeamPage";
import HostSettingsPage from "@/pages/HostSettingsPage";
import HostKnowledgeBasePage from "@/pages/HostKnowledgeBasePage";
import HostAIAgentsPage from "@/pages/HostAIAgentsPage";
import HostVoiceAgentPage from "@/pages/HostVoiceAgentPage";
import HostAssistantChatPage from "@/pages/HostAssistantChatPage";
import CreateGamePage from "@/pages/CreateGamePage";
import CreateSeriesPage from "@/pages/CreateSeriesPage";
import CreateTripPage from "@/pages/CreateTripPage";
import OnboardingPage from "@/pages/OnboardingPage";
import OnboardingSuccessPage from "@/pages/OnboardingSuccessPage";
import { useVisitTracker } from "@/hooks/useVisitTracker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sync user profile from backend when Firebase auth state changes
  // Renders immediately with Firebase info, then enhances in the background.
  const syncProfile = useCallback(async (firebaseUser) => {
    if (!firebaseUser) {
      setUser(null);
      setLoading(false);
      return;
    }

    // 1. Render UI instantly with Firebase basic info — no backend round-trip required
    const baseUser = {
      user_id: firebaseUser.uid,
      email: firebaseUser.email,
      name: firebaseUser.displayName || "Player",
      picture: firebaseUser.photoURL,
      is_admin: false,
      is_host: false,
      subscription_status: "none",
      plan: null,
    };
    setUser(baseUser);
    setLoading(false);

    // 2. Enhance with backend profile in background (admin flag, custom name, etc.)
    try {
      const token = await firebaseUser.getIdToken();
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 15000);
      const response = await fetch(`${API}/auth/me`, {
        headers: { "Authorization": `Bearer ${token}` },
        signal: ctrl.signal,
      });
      clearTimeout(timer);

      if (response.ok) {
        const profile = await response.json();
        setUser((prev) => ({
          ...(prev || baseUser),
          ...profile,
          name: profile.name || baseUser.name,
          email: profile.email || baseUser.email,
          picture: profile.picture || baseUser.picture,
        }));
      }
    } catch (error) {
      // Backend slow/down — Firebase user is already set, just log
      console.warn("Profile enhancement failed (using Firebase user only):", error?.message || error);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      syncProfile(firebaseUser);
    });
    return unsubscribe;
  }, [syncProfile]);

  const logout = useCallback(async () => {
    try {
      await auth.signOut();
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, logout, getAuthToken }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return children;
};

function AppRouter() {
  useVisitTracker();
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <OnboardingPage />
        </ProtectedRoute>
      } />
      <Route path="/onboarding/success" element={
        <ProtectedRoute>
          <OnboardingSuccessPage />
        </ProtectedRoute>
      } />
      <Route path="/game/:gameId" element={<GamePage />} />
      <Route path="/series/:seriesId" element={<SeriesPage />} />
      <Route path="/trips/:tripId" element={<TripPage />} />
      <Route path="/profile" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/host" element={
        <ProtectedRoute>
          <HostHomePage />
        </ProtectedRoute>
      } />
      <Route path="/host/manage/:type/:id" element={
        <ProtectedRoute>
          <HostManageEventPage />
        </ProtectedRoute>
      } />
      <Route path="/host/revenue" element={
        <ProtectedRoute>
          <HostRevenuePage />
        </ProtectedRoute>
      } />
      <Route path="/host/inbox" element={
        <ProtectedRoute>
          <HostInboxPage />
        </ProtectedRoute>
      } />
      <Route path="/host/events" element={
        <ProtectedRoute>
          <HostEventsPage />
        </ProtectedRoute>
      } />
      <Route path="/host/schedule" element={
        <ProtectedRoute>
          <HostSchedulePage />
        </ProtectedRoute>
      } />
      <Route path="/host/customers" element={
        <ProtectedRoute>
          <HostCustomersPage />
        </ProtectedRoute>
      } />
      <Route path="/host/customers/:id" element={
        <ProtectedRoute>
          <HostCustomerDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/host/attendees" element={<Navigate to="/host/customers" replace />} />
      <Route path="/host/memberships" element={
        <ProtectedRoute>
          <HostMembershipsPage />
        </ProtectedRoute>
      } />
      <Route path="/host/billing" element={
        <ProtectedRoute>
          <HostBillingPage />
        </ProtectedRoute>
      } />
      <Route path="/host/marketing" element={
        <ProtectedRoute>
          <HostMarketingPage />
        </ProtectedRoute>
      } />
      <Route path="/host/team" element={
        <ProtectedRoute>
          <HostTeamPage />
        </ProtectedRoute>
      } />
      <Route path="/host/settings" element={
        <ProtectedRoute>
          <HostSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/host/knowledge" element={
        <ProtectedRoute>
          <HostKnowledgeBasePage />
        </ProtectedRoute>
      } />
      <Route path="/host/ai" element={
        <ProtectedRoute>
          <HostAIAgentsPage />
        </ProtectedRoute>
      } />
      <Route path="/host/ai/voice" element={
        <ProtectedRoute>
          <HostVoiceAgentPage />
        </ProtectedRoute>
      } />
      <Route path="/host/ai/assistant" element={
        <ProtectedRoute>
          <HostAssistantChatPage />
        </ProtectedRoute>
      } />
      <Route path="/create/game" element={
        <ProtectedRoute>
          <CreateGamePage />
        </ProtectedRoute>
      } />
      <Route path="/create/series" element={
        <ProtectedRoute>
          <CreateSeriesPage />
        </ProtectedRoute>
      } />
      <Route path="/create/trip" element={
        <ProtectedRoute>
          <CreateTripPage />
        </ProtectedRoute>
      } />
      <Route path="/profile/:userId" element={<ProfilePage />} />
      <Route path="/dashboard" element={<Navigate to="/profile" replace />} />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminPage />
        </ProtectedRoute>
      } />
      <Route path="/memories" element={
        <ProtectedRoute>
          <MemoriesPage />
        </ProtectedRoute>
      } />
      <Route path="/payment/success" element={
        <ProtectedRoute>
          <PaymentSuccessPage />
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="App min-h-screen bg-background">
          <AppRouter />
          <Toaster position="top-center" richColors />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
